import { useRef, useState, type DragEvent } from 'react'
import { padLabel, type PadState } from './padState'

type Props = {
  index: number
  state: PadState
  isPlaying: boolean
  onFile: (index: number, file: File) => void
  onClick: (index: number) => void
  onClear: (index: number) => void
}

export default function Pad({ index, state, isPlaying, onFile, onClick, onClear }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!dragOver) setDragOver(true)
  }
  const handleDragLeave = () => setDragOver(false)
  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) onFile(index, file)
  }

  const handleClick = () => {
    if (state.status === 'ready') {
      onClick(index)
    } else if (state.status === 'empty' || state.status === 'error') {
      inputRef.current?.click()
    }
  }

  const classes = [
    'pad',
    `pad--${state.status}`,
    dragOver ? 'pad--dragover' : '',
    isPlaying ? 'pad--playing' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="pad-wrap">
      <button
        type="button"
        className={classes}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        aria-label={`Pad ${padLabel(index)}`}
      >
        {state.status === 'empty' && <span className="pad__num pad__num--big">{padLabel(index)}</span>}

        {state.status === 'encoding' && (
          <>
            <span className="pad__num">{padLabel(index)}</span>
            <span className="pad__spinner" aria-hidden="true" />
          </>
        )}

        {state.status === 'ready' && (
          <>
            <span className="pad__num">{padLabel(index)}</span>
            {isPlaying ? (
              <span className="pad__eq" aria-hidden="true">
                <i /><i /><i />
              </span>
            ) : (
              <span className="pad__play" aria-hidden="true">▶</span>
            )}
          </>
        )}

        {state.status === 'error' && (
          <>
            <span className="pad__num">{padLabel(index)}</span>
            <span className="pad__err" aria-hidden="true">!</span>
          </>
        )}
      </button>

      <div className="pad__caption">
        {state.status === 'empty' && <span className="pad__caption-empty">empty</span>}
        {state.status === 'encoding' && (
          <span className="pad__caption-name" title={state.sourceName}>
            {state.sourceName}
          </span>
        )}
        {state.status === 'ready' && (
          <>
            <span className="pad__caption-name" title={state.sourceName}>
              {state.sourceName}
            </span>
            <button
              type="button"
              className="pad__clear"
              onClick={(e) => {
                e.stopPropagation()
                onClear(index)
              }}
              aria-label={`Clear pad ${padLabel(index)}`}
            >
              ×
            </button>
          </>
        )}
        {state.status === 'error' && (
          <span className="pad__caption-err" title={state.message}>
            {state.message}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.wav,.mp3,.aac,.m4a"
        className="pad__input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile(index, file)
          e.target.value = ''
        }}
      />
    </div>
  )
}
